import React from 'react';
import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';
import { Loader2 } from 'lucide-react';

export interface CinematicButtonProps extends React.ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: 'primary' | 'secondary' | 'outline' | 'ghost' | 'danger';
  size?: 'sm' | 'md' | 'lg' | 'xl';
  glow?: boolean;
  loading?: boolean;
  leftIcon?: React.ReactNode;
  rightIcon?: React.ReactNode;
}

export const CinematicButton = React.forwardRef<HTMLButtonElement, CinematicButtonProps>(
  ({ 
    className, 
    variant = 'primary', 
    size = 'md', 
    glow = false,
    loading = false,
    leftIcon,
    rightIcon,
    children,
    disabled,
    ...props 
  }, ref) => {
    const baseStyles = 'rounded-full font-medium transition-all duration-300 ease-out inline-flex items-center justify-center gap-2';
    
    const variants = {
      primary: cn(
        'bg-primary text-primary-foreground hover:bg-primary/90',
        'hover:scale-[1.02] active:scale-[0.98]',
        glow && 'shadow-lg shadow-primary/20 hover:shadow-xl hover:shadow-primary/30'
      ),
      secondary: cn(
        'border border-border/30 bg-background/50 text-foreground',
        'hover:bg-foreground/5 hover:border-primary/30'
      ),
      outline: cn(
        'border border-border bg-transparent text-foreground',
        'hover:border-primary/50 hover:bg-primary/5'
      ),
      ghost: 'text-foreground hover:bg-muted',
      danger: cn(
        'bg-destructive text-destructive-foreground hover:bg-destructive/90',
        'hover:scale-[1.02] active:scale-[0.98]'
      ),
    };

    const sizes = {
      sm: 'px-4 py-2 text-sm h-9',
      md: 'px-6 py-2.5 text-sm h-11',
      lg: 'px-8 py-3 text-base h-12',
      xl: 'px-8 h-14 text-lg', // Hero button size
    };

    return (
      <Button
        ref={ref}
        className={cn(
          baseStyles,
          variants[variant],
          sizes[size],
          (disabled || loading) && 'opacity-60 cursor-not-allowed hover:scale-100',
          className
        )}
        disabled={disabled || loading}
        {...props}
      >
        {loading && <Loader2 className="w-4 h-4 animate-spin" />}
        {!loading && leftIcon}
        {children}
        {!loading && rightIcon}
      </Button>
    );
  }
);

CinematicButton.displayName = 'CinematicButton';

export default CinematicButton;
